import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Table,
  theme,
  Tooltip,
  Tag,
  Spin,
  Skeleton,
  Modal,
  Badge,
  message
} from 'antd'
import CustomRow from '../../../reusable-components/CustomRow'
import { useInvoices } from './hooks'
import { ClaimResponseModal } from './components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAdd,
  faCheckCircle,
  faEye,
  faMoneyBill,
  faPencil,
  faRefresh,
  faSearch,
  faPrint,
  faReceipt,
  faFilter,
  faSync,
  faTrash,
  faHardHat,
  faMedkit,
  faShieldAlt,
  faCar,
  faIdCard
} from '@fortawesome/free-solid-svg-icons'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../core/utils/reducer'
import CoreEnums from '../../../../core/utils/enums'
import dayjs from 'dayjs'
import InvoiceForm from './InvoiceForm'
import PatientPaymentForm from '../patient-payment-form'
import { ContainerCard } from '../../../reusable-components/AgiliteCards'
import PageHeader from '../../../reusable-components/PageHeader'

import ObjectID from 'bson-objectid'

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }

  .ant-table-thead th h2 {
    font-size: 15px !important;
    margin: 0;
  }
  
  .skeleton-cell {
    padding: 0 !important;
  }
  
  .skeleton-tag {
    display: flex;
    justify-content: center;
  }
  
  .skeleton-action {
    display: flex;
    justify-content: center;
    gap: 6px;
  }
`

/**
 * Component for displaying and managing invoices
 */
const InvoicesList = () => {
  const dispatch = useDispatch()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [record, setRecord] = useState(null)
  const [syncStatus, setSyncStatus] = useState(null)

  // Initialize the invoices list hook
  const {
    loading,
    data,
    page,
    pageSize,
    total,
    filters,
    setPage,
    setPageSize,
    setFilters,
    fetchInvoices,
    markInvoiceAsComplete,
    handlePrintInvoice,
    printingInvoiceId,
    checkInvoiceStatus,
    checkAllPendingStatuses
  } = useInvoices()

  // Fetch invoices on mount and when filters, page, or pageSize change
  useEffect(() => {
    fetchInvoices()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize])

  /**
   * Opens the invoice form in a new tab
   */
  const handleOpenInvoice = (record) => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.INVOICES}_${record._id}`,
        closable: true,
        label: `Invoice: ${record.fullName}`,
        children: <InvoiceForm data={record} isNew={false} handleGetInvoices={fetchInvoices} />
      })
    )
  }

  /**
   * Creates a new invoice in a new tab
   */
  const handleCreateInvoice = () => {
    // Use a unique key for new invoices to ensure the form is always fresh
    const uniqueKey = `${CoreEnums.tabKeys.INVOICES}_new_${new ObjectID().toString()}`
    dispatch(
      coreReducer.actions.addTab({
        key: uniqueKey,
        closable: true,
        label: 'New Invoice',
        children: <InvoiceForm data={{}} isNew={true} handleGetInvoices={fetchInvoices} />
      })
    )
  }

  /**
   * Creates a new patient payment for an invoice
   */
  const handleCreateNewPatientPayment = (record) => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.PATIENT_PAYMENTS}_new`,
        closable: true,
        label: 'New Patient Payment',
        children: <PatientPaymentForm data={record} />
      })
    )
  }

  /**
   * Opens claim response modal
   */
  const handleViewClaimResponses = (record) => {
    setIsModalOpen(true)
    setRecord(record)
  }

  const { token } = theme.useToken()

  // Button style for actions
  const buttonStyle = {
    height: '40px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  // Generate skeleton data for table
  const generateSkeletonData = () => {
    return Array(10)
      .fill()
      .map((_, index) => ({
        key: `skeleton-${index}`,
        _id: `skeleton-${index}`,
        skeleton: true,
        invoiceNo: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        fullName: <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        idNo: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        bookingDate: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        invoiceType: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        goodXStatus: (
          <div className='skeleton-tag'>
            <Skeleton.Input active size='small' style={{ width: '80%', borderRadius: '16px' }} />
          </div>
        ),
        status: (
          <div className='skeleton-tag'>
            <Skeleton.Input active size='small' style={{ width: '80%', borderRadius: '16px' }} />
          </div>
        ),
        createdAt: <Skeleton.Input active size='small' style={{ width: '90%' }} />,
        actions: (
          <div className='skeleton-action'>
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
            <Skeleton.Button active size='small' style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
          </div>
        )
      }))
  }

  // Define table columns
  const columns = [
    {
      title: <h2>Invoice Number</h2>,
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: '10%',
      sorter: loading
        ? false
        : (a, b) => {
            const numA = parseInt(a.invoiceNo, 10)
            const numB = parseInt(b.invoiceNo, 10)
            return numA - numB
          },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Full Name</h2>,
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: loading ? false : (a, b) => a.fullName.localeCompare(b.fullName),
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>ID Number</h2>,
      dataIndex: 'idNo',
      key: 'idNo',
      sorter: loading ? false : (a, b) => a.idNo.localeCompare(b.idNo),
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Treatment Date</h2>,
      dataIndex: 'bookingDate',
      key: 'bookingDate',
      sorter: loading
        ? false
        : (a, b) => {
            if (a.bookingDate === 'N/A' && b.bookingDate === 'N/A') return 0
            if (a.bookingDate === 'N/A') return -1
            if (b.bookingDate === 'N/A') return 1
            return new Date(a.bookingDate) - new Date(b.bookingDate)
          },
      width: '10%',
      render: (text, record) => {
        if (record.skeleton) return text
        return record.bookingDate === 'N/A' ? 'N/A' : dayjs(text).format('YYYY-MM-DD')
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Invoice Type</h2>,
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      filters: loading
        ? null
        : [
            { text: 'Medical Aid', value: 'medical' },
            { text: 'Insurance', value: 'insurance' },
            { text: 'Private', value: 'private' }
          ],
      onFilter: (value, record) => {
        if (record.skeleton) return true
        return record.invoiceType === value
      },
      render: (text, record) => {
        if (record.skeleton) return text
        switch (text) {
          case 'medical':
            return 'Medical Aid'
          case 'insurance':
            return 'Insurance'
          case 'private':
            return 'Private'
          default:
            return text
        }
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Claim Type</h2>,
      dataIndex: 'claimType',
      key: 'claimType',
      width: '10%',
      filters: loading
        ? null
        : [
            { text: 'Medical Aid', value: 'medical_aid' },
            { text: 'COID', value: 'COID' },
            { text: 'FEM', value: 'FEM' },
            { text: 'SAPS', value: 'SAPS' },
            { text: 'RMA', value: 'RMA' }
          ],
      onFilter: (value, record) => {
        if (record.skeleton) return true
        return record.claimType === value
      },
      render: (text, record) => {
        if (record.skeleton) return <Skeleton.Input active size='small' style={{ width: '90%' }} />

        // Handle compensation fund claims
        switch (text) {
          case 'COID':
            return (
              <Tag color='#1890ff'>
                <FontAwesomeIcon icon={faHardHat} style={{ marginRight: '4px' }} />
                COID
              </Tag>
            )
          case 'FEM':
            return (
              <Tag color='#52c41a'>
                <FontAwesomeIcon icon={faMedkit} style={{ marginRight: '4px' }} />
                FEM
              </Tag>
            )
          case 'SAPS':
            return (
              <Tag color='#722ed1'>
                <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '4px' }} />
                SAPS
              </Tag>
            )
          case 'RMA':
            return (
              <Tag color='#fa8c16'>
                <FontAwesomeIcon icon={faCar} style={{ marginRight: '4px' }} />
                RMA
              </Tag>
            )
          case 'medical_aid':
          default:
            return (
              <Tag color='cyan'>
                <FontAwesomeIcon icon={faIdCard} style={{ marginRight: '4px' }} />
                Medical Aid
              </Tag>
            )
        }
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Switch Status</h2>,
      dataIndex: 'goodXStatus',
      key: 'goodXStatus',
      sorter: loading ? false : (a, b) => a.goodXStatus?.localeCompare(b.goodXStatus || ''),
      render: (text, record) => {
        if (record.skeleton) return text

        if (record.status === 'draft') {
          return (
            <center>
              <Tag color='#faad14'>N/A</Tag>
            </center>
          )
        }

        if (!text)
          return (
            <center>
              <Tag color='#1890ff'>Submitted</Tag>
            </center>
          )

        const returnText = text
          .split(/[_\s]/) // Split by underscore or space
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')

        // Custom color mapping for different statuses
        const statusColorMap = {
          'Processed Successful': '#52c41a', // green
          Processed: '#52c41a', // green
          Processing: '#faad14', // gold/orange
          Failed: '#f5222d', // red
          Rejected: '#f5222d', // red
          Pending: '#faad14' // gold/orange
        }

        const color = statusColorMap[returnText] || '#faad14'

        return (
          <center>
            <Tag color={color}>{returnText === 'Processed Successful' ? 'Processed' : returnText}</Tag>
          </center>
        )
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Invoice Status</h2>,
      dataIndex: 'status',
      key: 'status',
      sorter: loading ? false : (a, b) => a.status.localeCompare(b.status),
      render: (text, record, index) => {
        if (record.skeleton) return text

        return (
          <center>
            {text === 'completed' ? (
              <FontAwesomeIcon style={{ fontSize: 18 }} icon={faCheckCircle} color='#52c41a' />
            ) : text === 'pending' || !text ? (
              <Tag color='#faad14'>Pending</Tag>
            ) : text === 'draft' ? (
              <Tag color='#722ed1'>Draft</Tag>
            ) : (
              <Tag color='#1890ff'>{text}</Tag>
            )}
          </center>
        )
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Creation Date</h2>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, record) => {
        if (record.skeleton) return text
        return dayjs(text).format('YYYY-MM-DD HH:mm:ss')
      },
      sorter: loading ? false : (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      width: '10%',
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    },
    {
      title: <h2>Actions</h2>,
      key: 'actions',
      width: '280px',
      align: 'center',
      fixed: 'right',
      render: (_, record, index) => {
        if (record.skeleton) return record.actions

        return (
          <Space size={6} style={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap' }}>
            <Tooltip title='View/Edit Invoice'>
              <Button
                type='primary'
                size='small'
                onClick={() => handleOpenInvoice(record)}
                icon={<FontAwesomeIcon icon={faPencil} />}
                style={{
                  height: '28px',
                  width: '28px',
                  minWidth: '28px',
                  background: token.actionColor || token.colorPrimary,
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              />
            </Tooltip>

            <Tooltip title='View Claim Responses'>
              <Button
                type='primary'
                size='small'
                onClick={() => {
                  if (record.goodXStatus === 'Processed_successful') {
                    handleViewClaimResponses(record)
                  }
                }}
                icon={<FontAwesomeIcon icon={faEye} />}
                disabled={record.goodXStatus !== 'Processed_successful'}
                style={{
                  height: '28px',
                  width: '28px',
                  minWidth: '28px',
                  background:
                    record.goodXStatus === 'Processed_successful' ? token.actionColor || token.colorPrimary : '#f0f0f0',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: record.goodXStatus === 'Processed_successful' ? 1 : 0.5,
                  cursor: record.goodXStatus === 'Processed_successful' ? 'pointer' : 'not-allowed'
                }}
              />
            </Tooltip>

            <Tooltip title='Check Claim Status'>
              <Button
                type='primary'
                size='small'
                onClick={() => checkInvoiceStatus(record)}
                icon={<FontAwesomeIcon icon={faSync} />}
                disabled={!record.goodXTicketNo || record.goodXStatus === 'Private'}
                style={{
                  height: '28px',
                  width: '28px',
                  minWidth: '28px',
                  background: record.goodXTicketNo && record.goodXStatus !== 'Private' ? '#1890ff' : '#f0f0f0',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: record.goodXTicketNo && record.goodXStatus !== 'Private' ? 1 : 0.5,
                  cursor: record.goodXTicketNo && record.goodXStatus !== 'Private' ? 'pointer' : 'not-allowed'
                }}
              />
            </Tooltip>

            <Tooltip title='Print Invoice'>
              {printingInvoiceId === record._id ? (
                <Button
                  type='primary'
                  size='small'
                  style={{
                    height: '28px',
                    width: '28px',
                    minWidth: '28px',
                    background: token.actionColor || token.colorPrimary,
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                >
                  <Spin size='small' />
                </Button>
              ) : (
                <Button
                  type='primary'
                  size='small'
                  onClick={() => handlePrintInvoice(record)}
                  icon={<FontAwesomeIcon icon={faPrint} />}
                  style={{
                    height: '28px',
                    width: '28px',
                    minWidth: '28px',
                    background: token.actionColor || token.colorPrimary,
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                />
              )}
            </Tooltip>

            <Tooltip title='Allocate Payment'>
              <Button
                type='primary'
                size='small'
                onClick={() => handleCreateNewPatientPayment(record)}
                icon={<FontAwesomeIcon icon={faMoneyBill} />}
                style={{
                  height: '28px',
                  width: '28px',
                  minWidth: '28px',
                  background: token.actionColor || token.colorPrimary,
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
              />
            </Tooltip>

            <Tooltip title={record.status === 'completed' ? 'Already Completed' : 'Mark as Completed'}>
              <Button
                type='primary'
                size='small'
                onClick={() => record.status !== 'completed' && markInvoiceAsComplete(record._id)}
                icon={<FontAwesomeIcon icon={faCheckCircle} />}
                disabled={record.status === 'completed'}
                style={{
                  height: '28px',
                  width: '28px',
                  minWidth: '28px',
                  background: record.status === 'completed' ? '#f0f0f0' : '#52c41a',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: record.status === 'completed' ? 0.5 : 1,
                  cursor: record.status === 'completed' ? 'not-allowed' : 'pointer'
                }}
              />
            </Tooltip>
          </Space>
        )
      },
      className: (record) => (record.skeleton ? 'skeleton-cell' : '')
    }
  ]

  return (
    <ContainerCard bodyStyle={{ padding: 0 }}>
      <style>{spinAnimation}</style>
      <PageHeader title='Invoices Management' icon={faReceipt} />

      <div style={{ padding: '16px 24px' }}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
              <Col xs={24} sm={14} md={16} lg={16} xl={16}>
                <Space wrap>
                  <Input
                    placeholder='Search invoices...'
                    prefix={
                      <span
                        style={{
                          background: token.actionColor || token.colorPrimary,
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                      </span>
                    }
                    allowClear
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    style={{
                      width: '300px',
                      maxWidth: '100%',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                  />
                  <DatePicker
                    placeholder='Date'
                    style={{
                      width: '120px',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    value={filters.createdAt ? dayjs(filters.createdAt) : null}
                    onChange={(date) => {
                      setFilters({ ...filters, createdAt: date })
                    }}
                    allowClear
                  />
                  <Select
                    placeholder='Status'
                    style={{
                      width: '120px',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    allowClear
                    value={filters.status}
                    options={[
                      { label: 'Pending', value: 'pending' },
                      { label: 'Completed', value: 'completed' },
                      { label: 'Draft', value: 'draft' }
                    ]}
                    onChange={(value) => setFilters({ ...filters, status: value })}
                  />
                  <Select
                    placeholder='Invoice Type'
                    style={{
                      width: '150px',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    allowClear
                    value={filters.invoiceType}
                    options={[
                      { label: 'Medical', value: 'medical' },
                      { label: 'Insurance', value: 'insurance' },
                      { label: 'Private', value: 'private' }
                    ]}
                    onChange={(value) => setFilters({ ...filters, invoiceType: value })}
                  />
                </Space>
              </Col>
              <Col xs={24} sm={10} md={8} lg={8} xl={8}>
                <Row gutter={[12, 8]} justify='end'>
                  <Col>
                    <Tooltip title='Refresh invoice list'>
                      <Button
                        icon={
                          <FontAwesomeIcon
                            icon={faRefresh}
                            style={{
                              color: token.actionColor || token.colorPrimary,
                              animation: loading ? 'spin 1s linear infinite' : 'none',
                              marginRight: 8
                            }}
                          />
                        }
                        onClick={fetchInvoices}
                        disabled={loading}
                        type='default'
                        size='large'
                        style={{
                          ...buttonStyle,
                          background: 'rgba(24, 144, 255, 0.1)',
                          borderColor: 'rgba(24, 144, 255, 0.3)',
                          color: token.actionColor || token.colorPrimary
                        }}
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                  </Col>

                  <Col>
                    <Tooltip title='Check status for all pending invoices'>
                      <Button
                        icon={
                          <FontAwesomeIcon
                            icon={faSync}
                            style={{
                              color: '#52c41a',
                              marginRight: 8
                            }}
                          />
                        }
                        onClick={checkAllPendingStatuses}
                        disabled={loading}
                        type='default'
                        size='large'
                        style={{
                          ...buttonStyle,
                          background: 'rgba(82, 196, 26, 0.1)',
                          borderColor: 'rgba(82, 196, 26, 0.3)',
                          color: '#52c41a'
                        }}
                      >
                        Check All Status
                      </Button>
                    </Tooltip>
                  </Col>

                  <Col>
                    <Button
                      type='primary'
                      style={{
                        ...buttonStyle,
                        background: token.actionColor || token.colorPrimary,
                        borderColor: token.actionColor || token.colorPrimary
                      }}
                      onClick={handleCreateInvoice}
                      icon={<FontAwesomeIcon icon={faAdd} />}
                      size='large'
                    >
                      New Invoice
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Table
              loading={false}
              dataSource={loading ? generateSkeletonData() : data}
              bordered
              size='small'
              rowKey='_id'
              columns={columns}
              pagination={{
                total: total,
                current: page,
                pageSize: pageSize,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} invoices`,
                position: ['bottomCenter'],
                pageSizeOptions: ['10', '20', '50', '100'],
                style: { marginTop: 16 }
              }}
              onChange={(pagination, filters, sorter) => {
                if (!loading) {
                  setPage(pagination.current)
                  setPageSize(pagination.pageSize)
                }
              }}
              scroll={{ x: 1400 }}
              style={{
                borderRadius: 8,
                overflow: 'hidden'
              }}
            />
          </Col>
        </Row>
      </div>

      <ClaimResponseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setRecord(null)
        }}
        record={record}
      />
    </ContainerCard>
  )
}

export default InvoicesList
